import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { checkPasswordStrength } from '@zenlayer/portal-utils/esm/function/utils';
import { Form, Icon, Input, Space, Tooltip, Typography, } from '@zenlayer/zen-design';
import { NameIdSpace } from '@zenlayer/zen-design/lib/table';
import t from '@zenlayer/zen-intl18';
import { useBoolean, useCreation, useDebounceEffect } from 'ahooks';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import '@zenlayer/zen-design-icons/assets/EyeInvisibleOutlined.svg';
import '@zenlayer/zen-design-icons/assets/EyeOutlined.svg';
import { pickDomAttrs } from '@zenlayer/zen-design/esm/_util/utils';
import cls from './index.module.less';
// todo: adapt to form
const WarpFormItem = ({ children, ...props }) => {
    if (props.name) {
        return _jsx(Form.Item, { ...props, children: children });
    }
    return children;
};
export const Password = forwardRef(({ validate, onChange, value, defaultValue, label, disabled, disableAutoGenerate, disableVisible, disableOnChange, isTouched, replaceGeneratePasswordFn, htmlNameA, htmlNameB, dataAttrs, ...props }, ref) => {
    const [visible, { toggle, setTrue }] = useBoolean(false);
    const [_, refresh] = useState();
    const forceRefresh = () => {
        refresh(Math.random());
    };
    const defaultPwd = typeof defaultValue === 'string' ? defaultValue : defaultValue?.password;
    const defaultPwd2 = typeof defaultValue === 'string' ? defaultValue : defaultValue?.password2;
    const [pwd1, setPwd1] = useState(defaultPwd ?? '');
    const [pwd2, setPwd2] = useState(defaultPwd2 ?? '');
    const passwordStrength = validate?.strength ?? 4;
    const pwd1Touched = useRef(false);
    const pwd2Touched = useRef(false);
    const pwd1Ref = useRef(null);
    const pwd2Ref = useRef(null);
    const setRandomPwd = () => {
        if (disabled) {
            return;
        }
        let pwd = replaceGeneratePasswordFn?.() ?? generatePwd();
        pwd1Touched.current = true;
        pwd2Touched.current = true;
        setTrue();
        setPwd1(pwd);
        setPwd2(pwd);
    };
    const { htmlType, visibleIcon } = useCreation(() => {
        if (disableVisible) {
            return {
                htmlType: 'password',
                visibleIcon: undefined,
            };
        }
        return {
            htmlType: visible ? 'text' : 'password',
            visibleIcon: visible ? (_jsx(Icon, { type: "EyeOutlined", block: true, className: cls.inputSuffixIcon_icon, onClick: () => !disabled && toggle() })) : (_jsx(Icon, { type: "EyeInvisibleOutlined", block: true, className: cls.inputSuffixIcon_icon, onClick: () => !disabled && toggle() })),
        };
    }, [visible, disableVisible]);
    const error1 = useCreation(() => {
        const errorMsg = [];
        try {
            validate?.before?.(pwd1);
            validate?.replace != undefined
                ? validate?.replace?.(pwd1)
                : validatePwd(pwd1);
            validate?.after?.(pwd1);
            if (passwordStrength &&
                checkPasswordStrength(pwd1) < passwordStrength) {
                throw t('ASUT7cFpkLxQ');
            }
        }
        catch (error) {
            errorMsg.push(error);
        }
        return errorMsg.length === 0 ? undefined : errorMsg;
    }, [pwd1, validate?.revalidateKey]);
    const error2 = pwd1 === pwd2 ? undefined : t('REmR7_tTnF3T');
    const showError1 = pwd1Touched.current && !!error1;
    const showError2 = pwd2Touched.current && !!error2;
    useDebounceEffect(() => {
        if (!disableOnChange) {
            onChange?.({
                password: pwd1,
                password2: pwd2,
                isValid: !!pwd1 && !error1 && !error2,
            });
        }
    }, [disableOnChange, pwd1, pwd2, error1, error2], {
        wait: 100,
    });
    useEffect(() => {
        if (value !== pwd1 || value !== pwd2) {
            const newValue = typeof value === 'string' ? value : value?.password;
            const newValue2 = typeof value === 'string' ? value : value?.password2;
            setPwd1(newValue || '');
            setPwd2(newValue2 || '');
        }
    }, [value]);
    useEffect(() => {
        pwd1Touched.current = isTouched ?? false;
        pwd2Touched.current = isTouched ?? false;
        forceRefresh();
    }, [isTouched]);
    useImperativeHandle(ref, () => ({
        reset: () => {
            pwd1Touched.current = false;
            pwd2Touched.current = false;
            forceRefresh();
        },
        password1: pwd1Ref.current,
        password2: pwd2Ref.current,
    }));
    return (_jsxs(Space, { ...props, direction: "vertical", block: true, style: { width: 472, ...props?.style }, children: [_jsxs(Space, { size: 4, direction: "vertical", block: true, children: [_jsx(Input, { ...pickDomAttrs(dataAttrs?.pwdA).data, ref: pwd1Ref, disabled: disabled, type: "filled", label: label?.password1Label ?? t('2yjX6YOpSVAk'), value: pwd1, className: cls.passwordInput, htmlType: htmlType, htmlName: htmlNameA, autoComplete: "one-time-code", suffix: _jsxs(Space, { size: "ll", children: [disableAutoGenerate ? null : (_jsx(NameIdSpace, { isLinkStyle: true, name: _jsx(Tooltip, { title: t('8ACw5hTHwY9p'), getPopupContainer: (e) => e, children: _jsx("div", { className: cls.inputSuffixIcon, onClick: setRandomPwd, children: t('hAuXyXRNkNmH') }) }) })), visibleIcon] }), error: showError1, onChange: (v) => {
                            setPwd1(v);
                            if (!pwd1Touched.current) {
                                pwd1Touched.current = true;
                            }
                        } }), showError1 && (_jsx(Typography.Text, { type: "error", size: "12-16", children: error1.map((item, idx) => (_jsxs(Space, { align: "start", size: 4, block: true, children: [error1.length > 1 && _jsxs("div", { children: [idx + 1, "."] }), _jsx("div", { children: item })] }, idx))) }))] }), _jsxs(Space, { size: 4, direction: "vertical", block: true, children: [_jsx(Input, { ...pickDomAttrs(dataAttrs?.pwdB).data, ref: pwd2Ref, disabled: disabled, type: "filled", label: label?.password2Label ?? t('gOE9SBG-XGmq'), className: cls.passwordInput, value: pwd2, htmlType: htmlType, htmlName: htmlNameB, autoComplete: "one-time-code", suffix: visibleIcon, error: showError2, onChange: (v) => {
                            setPwd2(v);
                            if (!pwd2Touched.current) {
                                pwd2Touched.current = true;
                            }
                        } }), showError2 && (_jsx(Typography.Text, { type: "error", size: "12-16", children: error2 }))] }), label?.extra !== undefined ? (label.extra) : (_jsx(Typography.Text, { children: t('-fSOhoW52PTQ') }))] }));
});
const getRandomChars = (chars, count) => {
    let result = '';
    for (let i = 0; i < count; i++) {
        result += chars[Math.floor(Math.random() * chars.length)];
    }
    return result;
};
const shuffle = (str) => {
    const arr = str.split('');
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr.join('');
};
const generatePwd = () => {
    const upperCaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowerCaseChars = 'abcdefghijklmnopqrstuvwxyz';
    const numberChars = '0123456789';
    const specialChars = '`~!@$^*-_=+|;:,.?';
    let password = '';
    password += getRandomChars(upperCaseChars, 3); // 随机添加3个大写字母
    password += getRandomChars(lowerCaseChars, 4); // 随机添加4个小写字母
    password += getRandomChars(numberChars, 3); // 随机添加3个数字
    password += getRandomChars(specialChars, 4); // 随机添加4个特殊字符
    // 随机打乱顺序, 并在首尾添加字母和数字，保证密码不是以特殊字符开头或结尾
    password =
        getRandomChars(upperCaseChars, 1) +
            shuffle(password) +
            getRandomChars(numberChars, 1);
    return password;
};
const validatePwd = (pwd) => {
    const reg = /^(?=.*[0-9])(?=.*[a-zA-Z])(?=.*[`~!@$^*\-_=+|;:,.?])[0-9a-zA-Z`~!@$^*\-_=+|;:,.?]{8,16}$/;
    if (!reg.test(pwd)) {
        throw t('iP-JEaejqdss');
    }
};
